/**
 * Recorder (m-DVR) health (ADR 0027): the latest camera channel and storage states of a device, its open
 * problems, and the settings that decide what is a problem (expected recording medium, installed camera
 * channels). Alarms are raised by the database after a debounce; this screen only shows and configures.
 * KVKK: device states only. ADR 0003: nothing here acts on the vehicle.
 */
import { DEVICE_HEALTH_LIMITS, DVR_MEDIA, cameraStateName, storageStateName, type DeviceHealth, type DvrMedium } from "@fts/core";
import { useEffect, useState } from "react";
import { FormError } from "../components/Dialog";
import { HelpTip } from "../components/HelpTip";
import { useFeedback } from "../components/Toast";
import { useI18n, type MessageKey } from "../i18n";
import { formatDateTime } from "../lib/format";
import { canManageFleet } from "../lib/permissions";
import { useSession } from "../state/session";

/** Channels the settings form offers (the database accepts 1–16). */
const FORM_CHANNELS = [1, 2, 3, 4, 5, 6, 7, 8];

/**
 * Recorder health of the tenant's devices, reloaded on demand and whenever `dep` changes (pass the page's
 * TenantData: its periodic refresh then refreshes the health too).
 */
export function useDeviceHealth(dep?: unknown): { list: DeviceHealth[] | null; reload: () => void } {
  const { provider, tenant } = useSession();
  const [list, setList] = useState<DeviceHealth[] | null>(null);
  const [n, setN] = useState(0);
  useEffect(() => {
    if (!tenant) return;
    let live = true;
    provider
      .listDeviceHealth(tenant.id)
      .then((x) => live && setList(x))
      .catch(() => live && setList([]));
    return () => {
      live = false;
    };
  }, [provider, tenant, n, dep]);
  return { list, reload: () => setN((x) => x + 1) };
}

/** What the recorder health states mean; timings from DEVICE_HEALTH_LIMITS (same constants as the database). */
export function DeviceHealthHelp({ align }: { align?: "start" | "end" } = {}) {
  const { t } = useI18n();
  return (
    <HelpTip topic={t("devices.health")} align={align}>
      {t("help.deviceHealth", {
        raise: Math.round(DEVICE_HEALTH_LIMITS.raiseAfterS / 60),
        clear: Math.round(DEVICE_HEALTH_LIMITS.clearAfterS / 60),
        stale: Math.round(DEVICE_HEALTH_LIMITS.staleAfterS / 60),
      })}
    </HelpTip>
  );
}

/** Devices list: "OK" / "N problems" / "—" (no recorder or no report). */
export function DeviceHealthBadge({ health }: { health: DeviceHealth | undefined }) {
  const { t } = useI18n();
  const evaluated = !!health && (health.effective.medium !== null || health.effective.cameras.length > 0);
  if (!health || !evaluated || !health.reportedAt) return <span className="muted">{t("common.empty")}</span>;
  if (health.issues.length === 0) return <span className="badge tone-ok">{t("dvr.ok")}</span>;
  const n = health.issues.length;
  return <span className="badge tone-crit">{n === 1 ? t("dvr.problemOne") : t("dvr.problems", { count: n })}</span>;
}

function issueText(t: ReturnType<typeof useI18n>["t"], h: DeviceHealth, key: string, state: number): string {
  if (key === "storage") return t("dvr.issue.storage", { medium: t(`dvr.medium.${h.effective.medium ?? "sd"}`), state: t(`dvr.storage.${storageStateName(state)}` as MessageKey) });
  const ch = Number(key.slice(3));
  const lost = h.videoLoss !== null && ((h.videoLoss >>> (ch - 1)) & 1) === 1;
  return t("dvr.issue.camera", { ch, state: lost && state === 0 ? t("dvr.videoLoss") : t(`dvr.camera.${cameraStateName(state)}` as MessageKey) });
}

/** Vehicle detail (device panel): states, open problems and the settings. */
export function DeviceHealthPanel({ deviceId, dep }: { deviceId: string; dep?: unknown }) {
  const { t, locale } = useI18n();
  const { provider, role } = useSession();
  const feedback = useFeedback();
  const { list, reload } = useDeviceHealth(dep);
  const h = list?.find((x) => x.deviceId === deviceId);
  const [editing, setEditing] = useState(false);
  if (!list) return null;
  if (!h) return null;
  const evaluated = h.effective.medium !== null || h.effective.cameras.length > 0;
  const canEdit = canManageFleet(role);
  if (!evaluated && !h.reportedAt && !canEdit) return null;
  const dash = t("common.empty");

  return (
    <section className="dvr-health" aria-label={t("dvr.title")}>
      <h3 className="dvr-subhead">{t("dvr.title")}</h3>
      {!h.reportedAt ? (
        <p className="panel-text muted">{evaluated ? t("dvr.noReport") : t("dvr.notRecorder")}</p>
      ) : (
        <>
          <p className="muted small">{t("dvr.reportedAt", { at: formatDateTime(h.reportedAt, locale) ?? "" })}</p>
          {h.issues.length > 0 ? (
            <ul className="dvr-issues" aria-label={t("dvr.openProblems")}>
              {h.issues.map((i) => (
                <li key={i.key} className="badge tone-crit">
                  {issueText(t, h, i.key, i.state)} · {t("dvr.since", { at: formatDateTime(i.since, locale) ?? "" })}
                  {i.alarmed ? ` · ${t("dvr.alarmed")}` : ""}
                  {/* Display only: no fresh status for this problem for a while (device off or silent). */}
                  {Date.now() - Date.parse(i.lastAt) > DEVICE_HEALTH_LIMITS.staleAfterS * 1000 ? ` · ${t("dvr.stale", { at: formatDateTime(i.lastAt, locale) ?? "" })}` : ""}
                </li>
              ))}
            </ul>
          ) : evaluated ? (
            <p className="panel-text">
              <span className="badge tone-ok">{t("dvr.ok")}</span>
            </p>
          ) : null}
          <dl className="dl">
            <div className="dl-row">
              <dt>{t("dvr.storageTitle")}</dt>
              <dd>
                {DVR_MEDIA.filter((m) => h.storage[m] !== undefined)
                  .map((m) => `${t(`dvr.medium.${m}`)}: ${t(`dvr.storage.${storageStateName(h.storage[m])}` as MessageKey)}${m === h.effective.medium ? ` (${t("dvr.expected")})` : ""}`)
                  .join(" · ") || dash}
              </dd>
            </div>
            <div className="dl-row">
              <dt>{t("dvr.camerasTitle")}</dt>
              <dd>
                {h.effective.cameras.length
                  ? h.effective.cameras
                      .map((ch) => `${t("dvr.channel", { ch })}: ${t(`dvr.camera.${cameraStateName(h.cameras[ch - 1])}` as MessageKey)}${h.videoLoss !== null && ((h.videoLoss >>> (ch - 1)) & 1) === 1 ? ` (${t("dvr.videoLoss")})` : ""}`)
                      .join(" · ")
                  : dash}
              </dd>
            </div>
          </dl>
        </>
      )}
      <p className="muted small">
        {t("dvr.settingsSummary", {
          medium: h.effective.medium ? t(`dvr.medium.${h.effective.medium}`) : t("dvr.none"),
          cameras: h.effective.cameras.length ? h.effective.cameras.join(", ") : t("dvr.none"),
        })}{" "}
        {t("dvr.fieldCaveat")}
      </p>
      {canEdit && !editing && (
        <button type="button" className="button button-secondary button-sm" onClick={() => setEditing(true)}>
          {t("dvr.editSettings")}
        </button>
      )}
      {canEdit && editing && (
        <DvrSettingsForm
          health={h}
          onCancel={() => setEditing(false)}
          onSave={async (medium, cameras) => {
            await provider.setDeviceDvrSettings(deviceId, { medium, cameras });
            feedback.ok(t("toast.dvrSaved"));
            setEditing(false);
            reload();
          }}
        />
      )}
    </section>
  );
}

function DvrSettingsForm({ health, onSave, onCancel }: { health: DeviceHealth; onSave: (m: DvrMedium | null, c: number[] | null) => Promise<void>; onCancel: () => void }) {
  const { t } = useI18n();
  const [medium, setMedium] = useState<string>(health.settings.medium ?? "");
  const [useDefault, setUseDefault] = useState(health.settings.cameras === null);
  const [cams, setCams] = useState<number[]>(health.settings.cameras ?? health.effective.cameras);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  return (
    <form
      className="dvr-settings"
      onSubmit={async (e) => {
        e.preventDefault();
        setBusy(true);
        setError(null);
        try {
          await onSave(medium ? (medium as DvrMedium) : null, useDefault ? null : cams);
        } catch (err) {
          setError(err);
        } finally {
          setBusy(false);
        }
      }}
    >
      <div className="field">
        <label htmlFor="dvr-medium">{t("dvr.mediumLabel")}</label>
        <select id="dvr-medium" value={medium} onChange={(e) => setMedium(e.target.value)}>
          <option value="">{t("dvr.default")}</option>
          {DVR_MEDIA.map((m) => (
            <option key={m} value={m}>
              {t(`dvr.medium.${m}`)}
            </option>
          ))}
        </select>
      </div>
      <fieldset className="chip-group">
        <legend>{t("dvr.camerasLabel")}</legend>
        <label className="check-row">
          <input type="checkbox" checked={useDefault} onChange={(e) => setUseDefault(e.target.checked)} />
          {t("dvr.default")}
        </label>
        {FORM_CHANNELS.map((ch) => (
          <label key={ch} className="check-row">
            <input
              type="checkbox"
              disabled={useDefault}
              checked={cams.includes(ch)}
              onChange={(e) => setCams(e.target.checked ? [...cams, ch].sort((a, b) => a - b) : cams.filter((x) => x !== ch))}
            />
            {t("dvr.channel", { ch })}
          </label>
        ))}
      </fieldset>
      {error !== null && <FormError error={error} />}
      <div className="form-actions">
        <button type="button" className="button button-secondary button-sm" onClick={onCancel} disabled={busy}>
          {t("common.cancel")}
        </button>
        <button type="submit" className="button button-primary button-sm" disabled={busy}>
          {t("common.save")}
        </button>
      </div>
    </form>
  );
}
